import { useState, useEffect, DragEvent, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  Download,
  Eye,
  Trash2,
  Upload,
  RefreshCcw,
  CheckSquare,
  Brain,
  FileUser,
} from "lucide-react";
import { toast } from "react-toastify";
import { useGetData } from "../../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../../interfaces/EvaluadoInterface";

import EvaluadoInfo from "../../../../components/EvaludoInfo";
import FileDropZone from "../../../../components/FileDropZone";
import usePdfPresencial from "../../../../hooks/usePdfPresencial";
import ConfirmarModal from "../../../../components/ConfirmarModal";
import ModalSeleccionPlantillas from "../../../../components/ModalSeleccionPlantillas";

export const PsicologicaListaPresencialScreen = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { uploadPDF, deletePDF, handleFile, fetchFilesData, handleMergePdfs } =
    usePdfPresencial();

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState("");
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileKeyToConfirm, setFileKeyToConfirm] = useState("");
  const [fileToConfirm, setFileToConfirm] = useState<any>(null);
  const [descriptionToConfirm, setDescriptionToConfirm] = useState<string>("");

  const [files, setFiles] = useState<{ [key: string]: File | string }>({
    file1: "Sin archivo seleccionado",
    file2: "Sin archivo seleccionado",
    file3: "Sin archivo seleccionado",
    file4: "Sin archivo seleccionado",
    file5: "Sin archivo seleccionado",
    file6: "Sin archivo seleccionado",
  });

  const [successFiles, setSuccessFiles] = useState<{ [key: string]: boolean }>({
    file1: false,
    file2: false,
    file3: false,
    file4: false,
    file5: false,
    file6: false,
  });

  // Actualizar el estado en localStorage para que ModulosListaPresnecialScreen pueda accederlo
  useEffect(() => {
    // Guardar el estado de cada archivo en localStorage
    if (evaluadosList_id) {
      Object.entries(files).forEach(([key, value]) => {
        // Solo guardamos si no es un objeto File (ya que no podemos guardar objetos complejos en localStorage)
        if (typeof value === 'string') {
          localStorage.setItem(`evaluacion_psicologica_${evaluadosList_id}_${key}`, value);
        }
      });
    }
  }, [files, evaluadosList_id]);

  useEffect(() => {
    if (evaluadosList_id) {
      fetchFilesData(
        setIsLoading,
        setFiles,
        evaluadosList_id,
        6,
        "evaluacion_psicologica"
      );
      if (fileUploaded) {
        setFileUploaded(false);
      }
    }
  }, [evaluadosList_id, token, fileUploaded]);

  const handleConfirmAction = (action: string, file: any, fileKey: string) => {
    setPendingAction(action);
    setFileToConfirm(file);
    setFileKeyToConfirm(fileKey);

    const module = modules.find((m) => m.fileKey === fileKey);
    setDescriptionToConfirm(
      module ? module.description : "Descripción no disponible."
    );

    setIsModalOpen(true);
  };

  const handleConfirm = (fileKey: string) => {
    if (
      !evaluadosList_id ||
      (!(fileToConfirm instanceof File) && typeof fileToConfirm !== "string")
    ) {
      toast.error(
        !evaluadosList_id
          ? "ID del evaluado no disponible."
          : "Archivo inválido."
      );
      return;
    }

    const module = modules.find((m) => m.fileKey === fileKey);
    if (!module) {
      console.log("Módulo no encontrado para el fileKey:", fileKey);
    }

    pendingAction === "eliminar"
      ? handleFileRemove(fileKey)
      : pendingAction === "subir" && handleUpload(fileKey);

    setIsModalOpen(false);
  };

  const handleCancel = () => setIsModalOpen(false);

  const handleUpload = async (fileKey: string) => {
    const file = files[fileKey];
    if (!(file instanceof File) || !evaluadosList_id) {
      return toast.error("ID del evaluado no disponible o archivo no válido.");
    }

    const match = fileKey.match(/\d+/);
    if (!match) return toast.error("Clave del archivo no válida.");

    const formattedFile = new File(
      [file],
      `${match[0].padStart(2, "0")}_${file.name}`,
      { type: file.type }
    );

    try {
      const { success, message } = await uploadPDF(
        evaluadosList_id,
        "evaluacion_psicologica",
        formattedFile
      );
      if (success) {
        setSuccessFiles((prev) => ({ ...prev, [fileKey]: true }));
        setFileUploaded(true);
        
        // Guardar en localStorage que el archivo se ha subido correctamente
        if (evaluadosList_id) {
          localStorage.setItem(`evaluacion_psicologica_${evaluadosList_id}_${fileKey}`, file.name);
        }
      } else {
        throw new Error(message || "Error desconocido.");
      }
    } catch {
      toast.error("Error al subir el archivo.");
      setFiles((prevFiles) => ({
        ...prevFiles,
        [fileKey]: "Sin archivo seleccionado",
      }));
      
      // Actualizar localStorage en caso de error
      if (evaluadosList_id) {
        localStorage.setItem(`evaluacion_psicologica_${evaluadosList_id}_${fileKey}`, "Sin archivo seleccionado");
      }
    }
  };

  const handleFileRemove = async (fileKey: string) => {
    const file = files[fileKey];
    if (!file || !evaluadosList_id) {
      toast.error(
        file
          ? "ID del evaluado no disponible."
          : "Archivo no válido o no seleccionado."
      );
      return;
    }

    const fileName = file instanceof File ? file.name : file;

    try {
      const { success } = await deletePDF(
        evaluadosList_id,
        "evaluacion_psicologica",
        fileName
      );
      if (success) {
        setFiles((prev) => ({
          ...prev,
          [fileKey]: "Sin archivo seleccionado",
        }));
        setSuccessFiles((prev) => ({ ...prev, [fileKey]: false }));
        setFileUploaded(false);
        
        // Actualizar localStorage al eliminar un archivo
        if (evaluadosList_id) {
          localStorage.setItem(`evaluacion_psicologica_${evaluadosList_id}_${fileKey}`, "Sin archivo seleccionado");
        }
      }
    } catch {
      toast.error("Hubo un problema al eliminar el archivo.");
    }
  };

  const procesarArchivo = async (
    fileKey: string,
    action: "view" | "download"
  ): Promise<void> => {
    const file = files[fileKey];
    const fileName = typeof file === "string" ? file : file?.name;

    if (!fileName || !evaluadosList_id) {
      toast.error(
        fileName
          ? "ID del evaluado no disponible."
          : "Archivo no válido o no seleccionado."
      );
      return;
    }

    try {
      await handleFile(
        evaluadosList_id,
        "evaluacion_psicologica",
        fileName,
        action
      );
    } catch {
      toast.error(
        `Hubo un error al intentar ${
          action === "view" ? "mostrar" : "descargar"
        } el archivo. Por favor, inténtelo de nuevo.`
      );
    }
  };

  const handleFileDrop = (e: DragEvent<HTMLDivElement>, fileKey: string) => {
    e.preventDefault(); // Prevenir el comportamiento predeterminado
    const file = e.dataTransfer.files?.[0]; // Obtener el archivo arrastrado

    if (file) {
      // Si se ha arrastrado un archivo, actualizamos el estado
      setFiles((prevFiles) => {
        const newFiles = {
          ...prevFiles,
          [fileKey]: file, // Actualizar el archivo en el estado
        };
        return newFiles;
      });
    } else {
      // Si no se arrastra ningún archivo, mostrar el texto por defecto
      setFiles((prevFiles) => {
        const newFiles = {
          ...prevFiles,
          [fileKey]: "Sin archivo seleccionado",
        };
        return newFiles;
      });
    }
  };

  const handleOpenFileSelector = (
    fileKey?: string,
    ref?: React.RefObject<HTMLInputElement>
  ) => {
    if (ref?.current) {
      // Usa el ref si está disponible
      ref.current.click();
    } else if (fileKey) {
      // Usa el fileKey para buscar el input por su ID
      const fileInput = document.getElementById(
        `file-input-${fileKey}`
      ) as HTMLInputElement;
      fileInput?.click();
    } else {
      console.error("No se proporcionó ni un ref ni un fileKey válido.");
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileKey: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFiles((prevFiles) => ({
        ...prevFiles,
        [fileKey]: file, // Sobrescribe el archivo previo
      }));
    } else {
      setFiles((prevFiles) => ({
        ...prevFiles,
        [fileKey]: "Sin archivo seleccionado", // Si no se selecciona nada, muestra el texto predeterminado
      }));
      
      // Actualizar localStorage
      if (evaluadosList_id) {
        localStorage.setItem(`evaluacion_psicologica_${evaluadosList_id}_${fileKey}`, "Sin archivo seleccionado");
      }
    }
  };

  const modules = [
    {
      title: "Autorización al Proceso de Evaluaciones Psicológicas",
      description: files.file1 instanceof File ? files.file1.name : files.file1,
      icon: <ClipboardList className="h-8 w-8" />,
      buttons: {
        create: { show: true },
      },
      fileKey: "file1",
    },
    {
      title: "Datos Generales",
      description: files.file2 instanceof File ? files.file2.name : files.file2,
      icon: <FileUser className="h-8 w-8" />,
      buttons: {
        create: { show: true },
      },
      fileKey: "file2",
    },
    {
      title: "Cuestionario de exploración personal",
      description: files.file3 instanceof File ? files.file3.name : files.file3,
      icon: <ClipboardList className="h-8 w-8" />,
      buttons: {
        create: { show: true },
      },
      fileKey: "file3",
    },
    {
      title: "Evaluación psicológica 2",
      description: files.file4 instanceof File ? files.file4.name : files.file4,
      icon: <Brain className="h-8 w-8" />,
      buttons: {
        create: { show: true },
      },
      fileKey: "file4",
    },
    {
      title: "Conclusiones de evaluación psicológica",
      description: files.file5 instanceof File ? files.file5.name : files.file5,
      icon: <CheckSquare className="h-8 w-8" />,
      buttons: {
        create: { show: true },
      },
      fileKey: "file5",
    },
    {
      title: "Llenada de conclusión psicológica",
      description: files.file6 instanceof File ? files.file6.name : files.file6,
      icon: <CheckSquare className="h-8 w-8" />,
      buttons: {
        create: { show: true },
      },
      fileKey: "file6",
    },
    {
      title: "Descargar formulario",
      description: "Archivo completo",
      icon: <Download className="h-8 w-8" />,
      buttons: {
        des: { show: true },
      },
      fileKey: "file7",
    },
  ];

  const allModulesAreGreen = modules.every((mod) => {
    return (
      mod.fileKey === "file7" || // Ignorar el módulo "Descargar formulario"
      (files[mod.fileKey] !== "Sin archivo seleccionado" &&
      !(files[mod.fileKey] instanceof File))
    );
  });

  return (
    <>
      <EvaluadoInfo />
      <div className="flex justify-between items-center px-8 py-4">
        <button
          onClick={() => {
            window.scrollTo(0, 0);
            navigate(`/modulos_presenciales/${queryEvaluado.data?.uuid}`);
          }}
          className="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 transition"
        >
          Regresar
        </button>
        <ModalSeleccionPlantillas carpeta={"evaluacion_psicologica"} />
      </div>

      <div className="container mx-auto">
        <h1 className="font-bold">Módulo Evaluación Psicológica (Presencial)</h1>
        <div>
          {isLoading ? (
            <div className="flex justify-center items-center py-4">
              <div
                className="spinner-border animate-spin inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
                role="status"
              >
                <span className="sr-only">Cargando...</span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
              {modules.map((module, index) => {
                // Si todos los módulos están en verde, mostramos el módulo "Descargar formulario"
                if (
                  module.title === "Descargar formulario" &&
                  !allModulesAreGreen
                ) {
                  return null; // No renderizamos este módulo si no todos están en verde
                }

                // Si todos los módulos están en verde, muestra el botón para "Descargar formulario"
                if (
                  allModulesAreGreen &&
                  module.title === "Descargar formulario"
                ) {
                  return (
                    <div
                      key={index}
                      className="border-3 rounded-lg p-4 shadow flex flex-col justify-between max-w-md w-full border-green-500"
                    >
                      <div className="mb-2 flex items-center gap-4">
                        {module.icon}
                        <h3 className="text-lg font-semibold mb-0">
                          {module.title}
                        </h3>
                      </div>
                      <p className="mb-2 text-gray-700">{module.description}</p>
                      <div className="flex justify-center space-x-4 mt-4">
                        <button
                          title="Descargar archivo"
                          onClick={() =>
                            handleMergePdfs(
                              evaluadosList_id ?? "defaultId", // Usar un valor predeterminado si es undefined
                              "evaluacion_psicologica", // Este valor debe venir de tus datos o props
                              `Modulo_Evaluacion_Psicologica_${queryEvaluado.data?.nombre}_${queryEvaluado.data?.apellido_paterno}_${queryEvaluado.data?.apellido_materno}.pdf`, // Este valor debe venir de tus datos o props
                              "download" // Acción "download" para descargar el archivo
                            )
                          }
                          className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition flex justify-center items-center"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        <button
                          title="Ver archivo"
                          onClick={() =>
                            handleMergePdfs(
                              evaluadosList_id ?? "defaultId", // Usar un valor predeterminado si es undefined
                              "evaluacion_psicologica", // Este valor debe venir de tus datos o props
                              `Modulo_Evaluacion_Psicologica_${queryEvaluado.data?.nombre}_${queryEvaluado.data?.apellido_paterno}_${queryEvaluado.data?.apellido_materno}.pdf`, // Este valor debe venir de tus datos o props
                              "view" // Acción "download" para descargar el archivo
                            )
                          }
                          className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition flex justify-center items-center"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={index}
                    className={`border-3 rounded-lg p-4 shadow flex flex-col justify-between max-w-md w-full ${
                      files[module.fileKey] === "Sin archivo seleccionado"
                        ? "border-red-500"
                        : files[module.fileKey] instanceof File
                        ? "border-yellow-300"
                        : "border-green-500"
                    }`}
                  >
                    <div className="flex flex-col items-left">
                      <div className="mb-4 flex items-center gap-2">
                        {module.icon}
                        <h3 className="text-base font-semibold mb-0 text-left">
                          {module.title}
                        </h3>
                      </div>
                    </div>

                    <p
                      className="mb-2 text-gray-700 break-words max-w-full"
                      style={{ wordBreak: "break-word", whiteSpace: "normal" }}
                      title={module.description}
                    >
                      {module.description}
                    </p>

                    {/* Solo renderizamos el área de arrastre si no hay archivo cargado */}
                    {files[module.fileKey] === "Sin archivo seleccionado" && (
                      <FileDropZone
                        module={module} // Pasar el objeto completo 'module', no solo 'module.fileKey'
                        handleFileDrop={handleFileDrop} // Asegúrate de pasar solo las funciones, no ejecutarlas
                        handleFileChange={handleFileChange} // Lo mismo aquí
                      />
                    )}

                    {/* Si el archivo está cargado (no "Sin archivo seleccionado") */}
                    {files[module.fileKey] !== "Sin archivo seleccionado" && (
                      <div className="flex justify-center space-x-4 mt-4">
                        {/* Botón de "Subir archivo" */}
                        {files[module.fileKey] &&
                          files[module.fileKey] instanceof File &&
                          module.buttons.create?.show && (
                            <button
                              title="Subir archivo"
                              onClick={() =>
                                handleConfirmAction(
                                  "subir",
                                  files[module.fileKey],
                                  module.fileKey
                                )
                              }
                              className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 flex justify-center items-center"
                            >
                              <Upload className="w-5 h-5" />
                            </button>
                          )}
                        {/* Botón de "Eliminar archivo" */}
                        {files[module.fileKey] &&
                          files[module.fileKey] !==
                            "Sin archivo seleccionado" &&
                          !(files[module.fileKey] instanceof File) && (
                            <button
                              title="Eliminar archivo"
                              onClick={() =>
                                handleConfirmAction(
                                  "eliminar",
                                  files[module.fileKey],
                                  module.fileKey
                                )
                              }
                              className="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 transition flex justify-center items-center"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}

                        {/* Botón de "Ver archivo" */}
                        {files[module.fileKey] &&
                          files[module.fileKey] !==
                            "Sin archivo seleccionado" &&
                          !(files[module.fileKey] instanceof File) && (
                            <button
                              title="Ver archivo"
                              onClick={() =>
                                procesarArchivo(module.fileKey, "view")
                              }
                              className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition flex justify-center items-center"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          )}

                        {/* Botón de "Descargar archivo" */}
                        {files[module.fileKey] &&
                          files[module.fileKey] !==
                            "Sin archivo seleccionado" &&
                          !(files[module.fileKey] instanceof File) && (
                            <button
                              title="Descargar archivo"
                              onClick={() =>
                                procesarArchivo(module.fileKey, "download")
                              }
                              className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition flex justify-center items-center"
                            >
                              <Download className="w-5 h-5" />
                            </button>
                          )}

                        {/* Botón "Cambiar archivo" */}
                        {files[module.fileKey] instanceof File && (
                          <>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={(e) =>
                                handleFileChange(e, module.fileKey)
                              }
                              className="hidden"
                            />
                            <button
                              title="Cambiar archivo"
                              onClick={() =>
                                handleOpenFileSelector(undefined, fileInputRef)
                              }
                              className="bg-indigo-500 text-white py-2 px-4 rounded hover:bg-indigo-600 transition flex justify-center items-center"
                            >
                              <RefreshCcw className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <ConfirmarModal
        isOpen={isModalOpen}
        action={pendingAction}
        description={descriptionToConfirm}
        onConfirm={() => handleConfirm(fileKeyToConfirm)}
        onCancel={handleCancel}
      />
    </>
  );
};